import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  InputSignalWithTransform,
  linkedSignal,
  numberAttribute,
  SecurityContext,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgTemplateOutlet } from '@angular/common';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { ConfigurationService } from '../../services/configuration/configuration.service';
import { IfFeatureService } from '../../services/if-feature/if-feature.service';

export type SymbolLogoTheme = 'light' | 'dark';
export type SymbolLogoFileTypes = 'svg' | 'png';

/**
 * This component will display a logo via a request to the new symbol logo webserver.
 * While the image is loading or if there's an error when loading, it will display the avatar component. It also supports a custom loading placeholder.
 * If there is a symbol with letter characters available, it will display the first alphabetical character available.
 * Otherwise, a blank avatar will be displayed.
 */
@Component({
  imports: [
    AvatarComponent,
    NgTemplateOutlet
  ],
  selector: 'td-wb-symbol-logo',
  templateUrl: 'symbol-logo.component.html',
  styleUrls: ['symbol-logo.component.scss'],
  host: {
    '[attr.aria-hidden]': 'true'
  }
})
export class SymbolLogoComponent {
  private static readonly DEFAULT_SIZE: number = 30;
  svgString: InputSignal<string> = input<string>();
  size: InputSignalWithTransform<number, unknown> =  input<number, unknown>(SymbolLogoComponent.DEFAULT_SIZE, { transform: numberAttribute });
  symbol: InputSignal<string | undefined> =  input<string>();
  description: InputSignal<string | undefined> =  input<string>();
  market: InputSignal<string | undefined> =  input<string>();
  theme: InputSignal<SymbolLogoTheme> =  input<SymbolLogoTheme>('light');
  fileType: InputSignal<SymbolLogoFileTypes> =  input<SymbolLogoFileTypes>('svg');
  customImageLoadingPlaceholder: InputSignal<boolean> = input<boolean>(false);

  imageLoadError: WritableSignal<boolean> = linkedSignal({
    source: computed(() => ({
      symbol: this.symbol(),
      market: this.market()
    })),
    computation: () => false
  });

  logoBaseUrl: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

  logoWebserverEnabled: WritableSignal<boolean> = signal<boolean>(false);

  hasImageLoaded: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly showLogo: Signal<boolean> = computed(() => {
    return this.symbol() !== undefined && this.symbol() !== '' &&
      this.market() !== undefined && this.market() !== '' &&
      this.logoBaseUrl() !== undefined && !this.imageLoadError();
  });

  protected readonly showOldLogo: Signal<boolean> = computed(() => {
    return this.svgString() && this.svgString() !== '';
  });

  private readonly sanitizer = inject(DomSanitizer);
  private readonly ifFeatureService = inject(IfFeatureService);
  private readonly configurationService = inject(ConfigurationService);

  constructor() {
    this.logoWebserverEnabled.set(this.ifFeatureService.getFeatureFlag<boolean>('symbolLogoWebserverEnabled'));
    this.logoBaseUrl.set(this.configurationService.getProperty('logoBaseUrl'));
  }

  getSVGImageUrlFromString(): SafeUrl {
    return this.sanitizer.sanitize(SecurityContext.URL, 'data:image/svg+xml;base64,' + this.svgString());
  }

  getSVGImageUrl(): SafeUrl {
    return this.sanitizer.sanitize(SecurityContext.URL, this.assembleSymbolLogoUrl());
  }

  /**
   * This function checks the first two characters of the symbol text for a letter for the Avatar component
   * If there is no letters present or if the symbol is undefined, then it falls back to checking the description
   */
  getInitials(): string {
    const firstTwoCharsofSymbol = this.symbol()?.substring(0, 2);
    let symbolText = (firstTwoCharsofSymbol?.match(/[a-z]/i) || [])?.pop();
    if(symbolText === undefined && this.description() !== undefined) {
      symbolText = (this.description()?.match(/[a-z]/i) || [])?.pop();
    }
    return symbolText ?? '';
  }

  onImageLoad(): void {
    this.hasImageLoaded.set(true);
  }

  onImageLoadError(): void {
    this.imageLoadError.set(true);
  }

  getSize(): string {
    return isNaN(Number(this.size())) ? SymbolLogoComponent.DEFAULT_SIZE + 'px' : this.size() + 'px';
  }

  private assembleSymbolLogoUrl(): string {
    return this.logoBaseUrl() + this.symbol() + '_' + this.market() + '_' + this.theme() + '.' + this.fileType();
  }
}
